import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { Phone, PhoneOff, Minus, GripHorizontal } from "lucide-react";

import { cn } from "../../lib/utils";
import useSegurar from "../../hooks/useSegurar";
import { useBotoesOcultos } from "../../ligacoes/botoesFlutuantes";
import OcultarBotoesLigacao from "../OcultarBotoesLigacao";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import useModules from "../../hooks/useModules";
import usePermissions from "../../hooks/usePermissions";
import useVolumeDoAlerta from "../../hooks/useVolumeDoAlerta";
import { criarToque } from "../../ligacoes/audio";
import {
	useLigacoes,
	configurar,
	recarregarConexoes,
	aoEvento,
	atender,
	recusar,
	desligar,
	alternarMudo,
	alternarEspera,
	ligar,
	abrirTelefone,
	minimizarTelefone,
} from "../../ligacoes/store";
import { Avatar, Cronometro, Ondas, nomeDe } from "./pecas";
import Discador from "./Discador";
import { TelaEmLigacao, TelaRecebida } from "./TelaChamada";

// Telefone de WhatsApp (módulo "calls"): flutua sobre qualquer tela, arrasta
// para o canto que a pessoa preferir e minimiza numa bolinha. Tem o discador,
// toca e abre sozinho quando chega ligação e mostra a ligação em andamento.

const CHAVE_POS = "ligacoes_whatsapp_pos";

const LigacoesWhatsApp = () => {
	const navigate = useNavigate();
	const { user } = useContext(AuthContext);
	const { temModulo, carregando: carregandoModulos } = useModules();
	const { temPermissao } = usePermissions();
	const { volume } = useVolumeDoAlerta();
	const lig = useLigacoes();
	const toque = useRef(null);
	const [ocupado, setOcupado] = useState(null);
	const [conexao, setConexao] = useState("");
	const [recentes, setRecentes] = useState([]);

	// ---------------------------------------------------------- arrastar
	// A posição é o único valor fora do Tailwind: muda a cada pixel do
	// arraste, então vai inline (left/top). Todo o resto é classe.
	const [pos, setPos] = useState(() => {
		try {
			const s = localStorage.getItem(CHAVE_POS);
			return s ? JSON.parse(s) : null;
		} catch {
			return null;
		}
	});
	const posRef = useRef(pos);
	const widgetRef = useRef(null);
	const arraste = useRef(null);
	const ocultos = useBotoesOcultos();
	const [pedirOcultar, setPedirOcultar] = useState(false);
	const segurar = useSegurar(() => {
		arraste.current = null;
		setPedirOcultar(true);
	});

	const aplicarPos = (np) => {
		posRef.current = np;
		setPos(np);
	};
	const limites = (x, y, w, h) => ({
		x: Math.max(8, Math.min(x, window.innerWidth - w - 8)),
		y: Math.max(8, Math.min(y, window.innerHeight - h - 8)),
	});
	const iniciarArraste = (e) => {
		if (e.button !== undefined && e.button !== 0) return;
		const el = widgetRef.current;
		if (!el) return;
		const r = el.getBoundingClientRect();
		arraste.current = {
			startX: e.clientX,
			startY: e.clientY,
			dx: e.clientX - r.left,
			dy: e.clientY - r.top,
			w: r.width,
			h: r.height,
			moveu: false,
		};
		e.currentTarget.setPointerCapture?.(e.pointerId);
		segurar.iniciar();
	};
	const moverArraste = (e) => {
		const a = arraste.current;
		if (!a) return;
		if (Math.hypot(e.clientX - a.startX, e.clientY - a.startY) > 4) {
			a.moveu = true;
			segurar.cancelar();
		}
		if (a.moveu) aplicarPos(limites(e.clientX - a.dx, e.clientY - a.dy, a.w, a.h));
	};
	const soltarArraste = (aoClicar) => {
		segurar.cancelar();
		const a = arraste.current;
		arraste.current = null;
		if (!a) return;
		if (a.moveu) {
			try {
				localStorage.setItem(CHAVE_POS, JSON.stringify(posRef.current));
			} catch {
				// sem storage: vale só nesta sessão
			}
		} else if (aoClicar) {
			aoClicar();
		}
	};

	// Abrir/fechar muda o tamanho: o que cabia como bolinha pode não caber
	// como painel naquele canto. Reencaixa na tela.
	useLayoutEffect(() => {
		if (!posRef.current || !widgetRef.current) return;
		const r = widgetRef.current.getBoundingClientRect();
		const c = limites(posRef.current.x, posRef.current.y, r.width, r.height);
		if (c.x !== posRef.current.x || c.y !== posRef.current.y) aplicarPos(c);
	});
	useEffect(() => {
		const aoRedimensionar = () => {
			if (!posRef.current || !widgetRef.current) return;
			const r = widgetRef.current.getBoundingClientRect();
			aplicarPos(limites(posRef.current.x, posRef.current.y, r.width, r.height));
		};
		window.addEventListener("resize", aoRedimensionar);
		return () => window.removeEventListener("resize", aoRedimensionar);
	}, []);

	// ---------------------------------------------------------- dados
	useEffect(() => {
		if (!user?.id || carregandoModulos) return;
		configurar({ userId: user.id, temModulo: temModulo("calls") });
	}, [user?.id, carregandoModulos, temModulo]);

	const carregarRecentes = useCallback(async () => {
		try {
			const { data } = await api.get("/calls", { params: { page: 1 } });
			setRecentes(data.calls.slice(0, 12));
		} catch {
			setRecentes([]);
		}
	}, []);

	useEffect(() => {
		if (lig.aberto && lig.disponivel) carregarRecentes();
	}, [lig.aberto, lig.disponivel, carregarRecentes]);

	useEffect(() => {
		if (!user?.id) return undefined;
		const socket = openSocket();
		socket.on("ligacao", (dados) => {
			const acao = aoEvento(dados);
			const l = dados.call;
			if (acao === "ended") carregarRecentes();
			const paraMim = !l.ringUserIds || l.ringUserIds.includes(user.id);
			if (acao === "incoming" && paraMim && document.hidden && window.Notification?.permission === "granted") {
				try {
					new Notification("Ligação recebida", {
						body: `${nomeDe(l)} · ${l.whatsapp?.name || ""}`,
						tag: `ligacao-${l.id}`,
					});
				} catch {
					// alguns navegadores só permitem via service worker
				}
			}
		});
		socket.on("ligacao-aparelho", () => recarregarConexoes());
		return () => socket.disconnect();
	}, [user?.id, carregarRecentes]);

	useEffect(() => {
		const padrao =
			lig.conexoes.find((c) => c.isDefault && c.paired) || lig.conexoes.find((c) => c.paired);
		setConexao((atual) =>
			atual && lig.conexoes.some((c) => String(c.id) === atual && c.paired)
				? atual
				: padrao
					? String(padrao.id)
					: ""
		);
	}, [lig.conexoes]);

	const podeAtender = temPermissao("calls:answer");
	const podeLigar = temPermissao("calls:make");

	// Tocando para mim: entrada ainda sem dono (ou minha), eu estou na lista de
	// quem ouve o toque e tenho permissão de atender.
	const tocando = Object.values(lig.chamadas).filter(
		(l) =>
			l.direction === "inbound" &&
			l.status === "ringing" &&
			!l.endedAt &&
			(!l.userId || l.userId === user?.id) &&
			(!l.ringUserIds || l.ringUserIds.includes(user?.id)) &&
			podeAtender &&
			lig.minha?.id !== l.id
	);
	const minha = lig.minha ? lig.chamadas[lig.minha.id] : null;
	const entrando = tocando[0];

	useEffect(() => {
		if (!toque.current) toque.current = criarToque();
		if (tocando.length) toque.current.iniciar(volume ?? 0.5);
		else toque.current.parar();
	}, [tocando.length, volume]);
	useEffect(() => () => toque.current?.parar(), []);

	const executar = async (chave, fn) => {
		setOcupado(chave);
		try {
			await fn();
		} catch (err) {
			if (err?.response) toastError(err);
			else toast.error(err?.message || "Falha na ligação.");
		}
		setOcupado(null);
	};

	if (!lig.disponivel && !minha && !tocando.length) return null;

	const estiloPos = pos ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" } : undefined;
	const posPadrao = pos ? "" : "bottom-40 right-4 md:bottom-24";

	// ---------------------------------------------------------- minimizado
	if (!lig.aberto) {
		// Em ligação: pílula com o contato, o tempo e o desligar à mão — sem
		// precisar abrir o painel para encerrar.
		if (minha) {
			const conectada = minha.status === "connected" && !minha.endedAt;
			return (
				<div
					ref={widgetRef}
					style={estiloPos}
					className={cn(
						"fixed z-40 flex items-center gap-2 rounded-full border bg-card/95 p-1.5 shadow-xl backdrop-blur supports-[backdrop-filter]:bg-card/80",
						posPadrao
					)}
				>
					<button
						type="button"
						onPointerDown={iniciarArraste}
						onPointerMove={moverArraste}
						onPointerUp={() => soltarArraste(() => abrirTelefone())}
						className="flex touch-none cursor-grab items-center gap-2 active:cursor-grabbing"
						title="Abrir a ligação · arraste para mover"
					>
						<Avatar nome={nomeDe(minha)} foto={minha.contact?.profilePicUrl} tamanho={36} />
						<span className="flex flex-col items-start pr-1 text-left leading-tight">
							<span className="max-w-[120px] truncate text-xs font-medium">{nomeDe(minha)}</span>
							<span className="text-[11px] text-emerald-600 dark:text-emerald-400">
								{conectada ? <Cronometro desde={minha.answeredAt} /> : "Chamando…"}
							</span>
						</span>
					</button>
					<button
						type="button"
						aria-label="Desligar"
						title="Desligar"
						disabled={!!ocupado}
						onClick={() => executar("desligar", () => desligar(minha.id))}
						className="flex size-9 items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600 active:scale-95"
					>
						<PhoneOff className="h-4 w-4" />
					</button>
				</div>
			);
		}

		// Escondido por escolha: só reaparece para ligação tocando.
		if (ocultos && !tocando.length) return null;

		return (
			<>
			<OcultarBotoesLigacao
				aberto={pedirOcultar}
				aoFechar={() => setPedirOcultar(false)}
			/>
			<button
				ref={widgetRef}
				type="button"
				onPointerDown={iniciarArraste}
				onPointerMove={moverArraste}
				onPointerUp={() => soltarArraste(() => abrirTelefone())}
				onContextMenu={(e) => e.preventDefault()}
				aria-label="Abrir telefone do WhatsApp (arraste para mover, segure para ocultar)"
				title="Telefone · clique para abrir, arraste para mover, segure para ocultar"
				style={estiloPos}
				className={cn(
					"fixed z-40 flex size-14 touch-none select-none cursor-grab items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-lg shadow-emerald-600/30 transition-transform hover:scale-105 active:cursor-grabbing",
					posPadrao
				)}
			>
				{tocando.length > 0 && <Ondas />}
				<Phone className="relative h-6 w-6" />
				{tocando.length > 0 && (
					<span className="absolute -right-0.5 -top-0.5 flex size-5 items-center justify-center rounded-full bg-red-500 text-[11px] font-bold ring-2 ring-card">
						{tocando.length}
					</span>
				)}
			</button>
			</>
		);
	}

	// ---------------------------------------------------------- aberto
	const linhasOk = lig.conexoes.filter((c) => c.paired).length;

	return (
		<div
			ref={widgetRef}
			style={estiloPos}
			className={cn(
				"fixed z-40 w-[320px] max-w-[calc(100vw-1rem)] overflow-hidden rounded-3xl border bg-card/95 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-card/85",
				posPadrao
			)}
		>
			<div
				className="relative flex cursor-grab touch-none select-none items-center justify-between px-4 pb-1 pt-3 active:cursor-grabbing"
				onPointerDown={iniciarArraste}
				onPointerMove={moverArraste}
				onPointerUp={() => soltarArraste()}
			>
				<GripHorizontal className="absolute left-1/2 top-1 h-4 w-4 -translate-x-1/2 text-muted-foreground/40" />
				<span className="flex items-center gap-2 text-xs text-muted-foreground">
					<span className={cn("size-2 rounded-full", linhasOk ? "bg-emerald-500" : "bg-amber-500")} />
					{linhasOk ? `Telefone · ${linhasOk} ${linhasOk === 1 ? "linha" : "linhas"}` : "Sem linha conectada"}
				</span>
				<button
					type="button"
					aria-label="Minimizar"
					title="Minimizar"
					onPointerDown={(e) => e.stopPropagation()}
					onClick={minimizarTelefone}
					className="flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
				>
					<Minus className="h-4 w-4" />
				</button>
			</div>

			{entrando && !minha ? (
				<TelaRecebida
					ligacao={entrando}
					ocupado={ocupado}
					onAtender={() => executar("atender", () => atender(entrando.id))}
					onRecusar={() => executar("recusar", () => recusar(entrando.id))}
				/>
			) : minha ? (
				<>
					<TelaEmLigacao
						ligacao={minha}
						estado={lig.minha}
						ocupado={ocupado}
						onMudo={alternarMudo}
						onEspera={() => executar("espera", () => alternarEspera())}
						onConversa={() => navigate(`/tickets/${minha.ticketId}`)}
						onDesligar={() => executar("desligar", () => desligar(minha.id))}
					/>
					{entrando && (
						<div className="mx-3 mb-3 flex items-center gap-2 rounded-2xl border bg-muted/50 p-2">
							<Avatar nome={nomeDe(entrando)} foto={entrando.contact?.profilePicUrl} tamanho={36} />
							<span className="min-w-0 flex-1">
								<span className="block truncate text-xs font-medium">{nomeDe(entrando)}</span>
								<span className="text-[11px] text-muted-foreground">outra ligação chegando</span>
							</span>
							<button
								type="button"
								aria-label="Recusar a outra ligação"
								onClick={() => executar("recusar", () => recusar(entrando.id))}
								className="flex size-8 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
							>
								<PhoneOff className="h-3.5 w-3.5" />
							</button>
							<button
								type="button"
								aria-label="Atender e pôr a atual em espera"
								title="Atender e pôr a atual em espera"
								onClick={() => executar("atender", () => atender(entrando.id))}
								className="flex size-8 items-center justify-center rounded-full bg-emerald-500 text-white hover:bg-emerald-600"
							>
								<Phone className="h-3.5 w-3.5" />
							</button>
						</div>
					)}
				</>
			) : (
				<Discador
					numero={lig.numero}
					conexoes={lig.conexoes}
					conexao={conexao}
					setConexao={setConexao}
					podeLigar={podeLigar}
					ocupado={ocupado === "ligar"}
					recentes={recentes}
					aoLigar={(numero) =>
						executar("ligar", () => ligar({ number: numero, whatsappId: Number(conexao) || undefined }))
					}
				/>
			)}
		</div>
	);
};

export default LigacoesWhatsApp;
